import React, { Suspense } from 'react';
import { Outlet, createFileRoute, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';

import { SalesOrderModalProvider } from '@/contexts/sales-order-modal-context';
import { SalesOrderModal } from '@/components/modals/sales-order/enhanced-modal';
import { DialogSuspense } from '@/components/modals/sales-order/suspense';

const searchSchema = z.object({
  salesOrderId: z.string().optional(),
  type: z.enum(['edit', 'view', 'add']).optional(),
  status: z
    .enum(['ALL'])
    .or(
      z.enum([
        'DRAFT',
        'AWAITING_PAYMENT',
        'PROCESSING',
        'SHIPPED',
        'DELIVERED',
        'COMPLETED',
        'CANCELLED',
        'REFUNDED',
      ])
    )
    .or(z.enum(['AWAITING_PREPARATION', 'TODAY', 'PREPARING', 'PRINTED', 'HOLD', 'PACKED', 'READY_TO_SHIP', 'SHIPPED']))
    .or(z.enum([
      'NOT_STARTED',
      'PICKED_UP',
      'IN_TRANSIT',
      'CUSTOMS_CLEARANCE',
      'OUT_FOR_DELIVERY',
      'DELIVERY_FAILED',
      'RETURNED'
    ]))
    .or(z.enum([
      'UNPAID',
      'PARTIALLY_PAID',
      'PAID',
      'REFUND_REQUESTED',
      'REFUNDED',
      'PARTIALLY_REFUNDED'
    ]))
    .or(z.enum([
      "ALL",
      "UNPAID",
      "PARTIALLY_PAID",
      "PAID",
      "REFUND_REQUESTED",
      "REFUNDED",
      "PARTIALLY_REFUNDED"
    ]))
    .optional(),
  needRefresh: z.boolean().optional(),
  dateRangeStart: z.string().optional(),
  dateRangeEnd: z.string().optional(),
  customerId: z.string().optional(),
  warehouseId: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  modalSize: z.string().optional(),
  modalType: z.enum([
    'warehouse',
    'logistic',
    'payment',
    'overview',
    'app'
  ]).optional(),
});

export const Route = createFileRoute('/_app/inventory/sales-order/_modal')({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
});

function RouteComponent() {
  const search = useSearch({
    from: '/_app/inventory/sales-order/_modal',
  });

  return (
    <SalesOrderModalProvider>
      <Outlet />
      {search.type && (search.salesOrderId || search.type === 'add') && (
        <Suspense fallback={<DialogSuspense />}>
          <SalesOrderModal />
        </Suspense>
      )}
    </SalesOrderModalProvider>
  );
}
